import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ApiError,
  CreditWeight,
  OrgBilling,
  OrgBillingUpdate,
  PricingTier,
  SweepResult,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

function money(n: number, dp = 2): string {
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;
}

function gb(n: number): string {
  return `${n.toLocaleString(undefined, {
    maximumFractionDigits: n < 10 ? 1 : 0,
  })} GB`;
}

function fmtDate(d: string | null): string {
  if (!d) return "-";
  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface OrgForm {
  tier_id: string;
  billing_start_date: string;
  monthly_credits: string;
  base_price_usd: string;
  overage_rate_usd: string;
  included_storage_gb: string;
  storage_overage_usd_per_gb: string;
  alert_threshold_pct: string;
}

/** Blank override inputs mean "inherit from the tier", which is how Enterprise
 *  negotiated pricing is expressed. Empty string round-trips to NULL. */
function toOrgForm(o: OrgBilling): OrgForm {
  return {
    tier_id: o.tier_id == null ? "" : String(o.tier_id),
    billing_start_date: o.billing_start_date ?? "",
    monthly_credits:
      o.monthly_credits_override == null
        ? ""
        : String(o.monthly_credits_override),
    base_price_usd:
      o.base_price_override == null ? "" : String(o.base_price_override),
    overage_rate_usd:
      o.overage_rate_override == null ? "" : String(o.overage_rate_override),
    included_storage_gb:
      o.storage_gb_override == null ? "" : String(o.storage_gb_override),
    storage_overage_usd_per_gb:
      o.storage_rate_override == null ? "" : String(o.storage_rate_override),
    alert_threshold_pct: String(o.alert_threshold_pct),
  };
}

function numOrNull(s: string): number | null {
  const t = s.trim();
  return t === "" ? null : Number(t);
}

export default function AdminBilling() {
  const me = useAuth((s) => s.user);
  const isOwner = me?.role === "owner";

  const [rows, setRows] = useState<OrgBilling[]>([]);
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [weights, setWeights] = useState<CreditWeight[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);

  const [editing, setEditing] = useState<OrgBilling | null>(null);
  const [form, setForm] = useState<OrgForm>({
    tier_id: "",
    billing_start_date: "",
    monthly_credits: "",
    base_price_usd: "",
    overage_rate_usd: "",
    included_storage_gb: "",
    storage_overage_usd_per_gb: "",
    alert_threshold_pct: "80",
  });
  const [saving, setSaving] = useState(false);

  // Credit-menu editor keeps its edits local until Save so a mistyped weight
  // can be abandoned without a round trip.
  const [weightDraft, setWeightDraft] = useState<Record<string, CreditWeight>>({});
  const [savingWeights, setSavingWeights] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    void load();
  }, [isOwner]);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      const [orgs, tierList, weightList] = await Promise.all([
        api.get<OrgBilling[]>("/admin/billing/orgs"),
        api.get<PricingTier[]>("/admin/tiers"),
        api.get<CreditWeight[]>("/admin/credit-weights"),
      ]);
      setRows(orgs);
      setTiers(tierList);
      setWeights(weightList);
      setWeightDraft({});
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load billing data");
    } finally {
      setBusy(false);
    }
  }

  function flashFor(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash((m) => (m === msg ? null : m)), 4000);
  }

  function openEdit(o: OrgBilling) {
    setEditing(o);
    setForm(toOrgForm(o));
    setErr(null);
  }

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    if (!editing) return;
    setSaving(true);
    setErr(null);
    const payload: OrgBillingUpdate = {
      tier_id: form.tier_id === "" ? null : Number(form.tier_id),
      billing_start_date: form.billing_start_date || null,
      monthly_credits: numOrNull(form.monthly_credits),
      base_price_usd: numOrNull(form.base_price_usd),
      overage_rate_usd: numOrNull(form.overage_rate_usd),
      included_storage_gb: numOrNull(form.included_storage_gb),
      storage_overage_usd_per_gb: numOrNull(form.storage_overage_usd_per_gb),
      alert_threshold_pct: Number(form.alert_threshold_pct || 80),
    };
    try {
      await api.patch<OrgBilling>(
        `/admin/billing/orgs/${editing.organization_id}`,
        payload,
      );
      flashFor(`Saved billing settings for ${editing.organization_name}.`);
      setEditing(null);
      await load();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to save billing settings");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(o: OrgBilling) {
    setErr(null);
    try {
      await api.patch(`/organizations/${o.organization_id}`, {
        is_active: !o.is_active,
      });
      flashFor(
        o.is_active
          ? `Deactivated ${o.organization_name}. Its users can no longer sign in and billing has stopped.`
          : `Reactivated ${o.organization_name}. Billing resumes at the next sweep.`,
      );
      await load();
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Failed to change organization state",
      );
    }
  }

  async function runSweep() {
    setSweeping(true);
    setErr(null);
    try {
      const r = await api.post<SweepResult>("/admin/billing/run-sweep");
      flashFor(
        `Sweep complete: ${r.periods_opened} period(s) opened, ${r.periods_closed} closed, ${r.invoices_created} invoice(s) drafted.`,
      );
      await load();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Sweep failed");
    } finally {
      setSweeping(false);
    }
  }

  const weightRows = useMemo(
    () => weights.map((w) => weightDraft[w.action_key] ?? w),
    [weights, weightDraft],
  );
  const weightsDirty = Object.keys(weightDraft).length > 0;

  function editWeight(w: CreditWeight, patch: Partial<CreditWeight>) {
    setWeightDraft((d) => ({
      ...d,
      [w.action_key]: { ...(d[w.action_key] ?? w), ...patch },
    }));
  }

  async function saveWeights() {
    setSavingWeights(true);
    setErr(null);
    try {
      // The API updates one action at a time; run them in sequence so a
      // failure part-way through leaves an obvious error next to the rows
      // that did not save (the table reloads from the server either way).
      for (const w of Object.values(weightDraft)) {
        await api.patch<CreditWeight>(
          `/admin/credit-weights/${encodeURIComponent(w.action_key)}`,
          {
            credits_per_unit: Number(w.credits_per_unit),
            is_billable: w.is_billable,
          },
        );
      }
      flashFor(
        "Credit menu saved. New weights apply to work performed from now on - already-recorded ledger entries keep the rate they were charged at.",
      );
      await load();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to save credit menu");
    } finally {
      setSavingWeights(false);
    }
  }

  if (!isOwner) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Billing</div>
            <h1>Billing &amp; credits</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          Billing administration is restricted to owners.
        </div>
      </div>
    );
  }

  const activeTiers = tiers.filter((t) => t.is_active);

  return (
    <div className="admin admin--page-scroll">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Billing</div>
          <h1>Billing &amp; credits</h1>
          <p className="admin__lead">
            Assign each customer a tier and a billing anniversary. Periods close
            and invoices draft automatically about once an hour; the manual
            sweep below is only for catching up immediately after a change.
          </p>
        </div>
        <div className="admin__head-actions">
          <button
            type="button"
            className="btn"
            onClick={() => void runSweep()}
            disabled={sweeping}
          >
            {sweeping ? "Running..." : "Run billing sweep"}
          </button>
          <button type="button" className="btn" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}
      {flash && <div className="alert alert--ok">{flash}</div>}

      <div className="admin__scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Organization</th>
              <th>Tier</th>
              <th>Anniversary</th>
              <th>Allowance</th>
              <th>Used</th>
              <th>Remaining</th>
              <th>Overage now</th>
              <th>Storage</th>
              <th>Lifetime credits</th>
              <th>Lifetime overage</th>
              <th>Cost / credit</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr>
                <td colSpan={12} className="muted">
                  Loading...
                </td>
              </tr>
            )}
            {!busy && rows.length === 0 && (
              <tr>
                <td colSpan={12} className="muted">
                  No organizations yet.
                </td>
              </tr>
            )}
            {rows.map((o) => (
              <tr
                key={o.organization_id}
                className={o.is_active ? "" : "admin-table__row--inactive"}
              >
                <td>
                  <div className="admin-table__name">
                    {o.organization_name}
                    {!o.is_active && (
                      <span className="admin-table__chip">deactivated</span>
                    )}
                  </div>
                </td>
                <td>
                  {o.tier_name ?? <span className="muted">no plan</span>}
                  {(o.monthly_credits_override != null ||
                    o.base_price_override != null ||
                    o.overage_rate_override != null ||
                    o.storage_gb_override != null ||
                    o.storage_rate_override != null) && (
                    <span
                      className="admin-table__chip"
                      title="This customer has custom pricing that overrides the tier."
                    >
                      custom
                    </span>
                  )}
                </td>
                <td className="billing-num">
                  {o.billing_start_date ? (
                    <>
                      day {new Date(`${o.billing_start_date}T00:00:00`).getDate()}
                      <div className="muted admin-table__mono">
                        since {fmtDate(o.billing_start_date)}
                      </div>
                    </>
                  ) : (
                    <span className="muted">not set</span>
                  )}
                </td>
                <td className="billing-num">
                  {o.included_credits.toLocaleString()}
                </td>
                <td className="billing-num">
                  {o.credits_used.toLocaleString()}
                  {o.included_credits > 0 && (
                    <div className="muted admin-table__mono">
                      {((o.credits_used / o.included_credits) * 100).toFixed(0)}%
                    </div>
                  )}
                </td>
                <td
                  className={`billing-num${o.remaining < 0 ? " billing-num--over" : ""}`}
                >
                  {o.remaining.toLocaleString()}
                </td>
                <td className="billing-num">
                  {o.overage_credits > 0 ? (
                    <>
                      {o.overage_credits.toLocaleString()}
                      <div className="muted admin-table__mono">credits</div>
                    </>
                  ) : (
                    <span className="muted">-</span>
                  )}
                </td>
                <td
                  className={`billing-num${
                    o.storage_overage_gb > 0 ? " billing-num--over" : ""
                  }`}
                >
                  {gb(o.on_plan ? o.peak_storage_gb : o.storage_gb)}
                  {o.on_plan ? (
                    // The peak is what bills, so it leads; the live figure is
                    // the one an operator gets asked about on a support call.
                    <div className="muted admin-table__mono">
                      peak of {gb(o.included_storage_gb)} · {gb(o.storage_gb)} now
                      {o.storage_overage_gb > 0 &&
                        ` · ${gb(o.storage_overage_gb)} over`}
                    </div>
                  ) : (
                    <div className="muted admin-table__mono">stored, unbilled</div>
                  )}
                </td>
                <td className="billing-num">
                  {o.lifetime_credits.toLocaleString()}
                </td>
                <td className="billing-num">
                  {o.lifetime_overage_credits > 0 ||
                  o.lifetime_storage_overage_gb > 0 ? (
                    <>
                      {o.lifetime_overage_credits.toLocaleString()}
                      <div className="muted admin-table__mono">
                        credits over, {o.overage_periods} period
                        {o.overage_periods === 1 ? "" : "s"}
                        {o.lifetime_storage_overage_gb > 0 &&
                          ` · ${gb(o.lifetime_storage_overage_gb)} storage`}
                      </div>
                    </>
                  ) : (
                    <span className="muted">none</span>
                  )}
                </td>
                <td className="billing-num">
                  {o.cost_per_credit == null ? (
                    <span className="muted">no data</span>
                  ) : (
                    <>
                      {money(o.cost_per_credit, 4)}
                      {o.margin_multiple != null && (
                        <div className="muted admin-table__mono">
                          {o.margin_multiple.toFixed(2)}x margin
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td className="admin-table__actions">
                  <Link
                    className="btn"
                    to={`/admin/usage?organization_id=${o.organization_id}`}
                    title="Month-by-month credit usage for this customer"
                  >
                    Usage
                  </Link>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => openEdit(o)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={o.is_active ? "btn btn--danger" : "btn"}
                    onClick={() => void toggleActive(o)}
                  >
                    {o.is_active ? "Deactivate" : "Reactivate"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---------- credit menu ---------- */}
      <div className="admin-card">
        <div className="admin-card__head">
          <div>
            <h2 className="admin-card__title">Credit menu</h2>
            <p className="admin-card__blurb">
              How many credits each kind of AI work consumes. Changes take
              effect immediately for new work; entries already in the ledger
              keep the rate they were charged at, so history stays intact.
            </p>
          </div>
          <div className="admin-card__actions">
            <button
              type="button"
              className="btn"
              onClick={() => setWeightDraft({})}
              disabled={!weightsDirty || savingWeights}
            >
              Discard changes
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void saveWeights()}
              disabled={!weightsDirty || savingWeights}
            >
              {savingWeights ? "Saving..." : "Save credit menu"}
            </button>
          </div>
        </div>

        <div className="admin__scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Activity</th>
                <th>Unit</th>
                <th>Credits per unit</th>
                <th>Billable</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {weightRows.map((w) => {
                const dirty = weightDraft[w.action_key] != null;
                return (
                  <tr key={w.action_key} className={dirty ? "admin-table__row--dirty" : ""}>
                    <td>
                      <div className="admin-table__name">{w.label}</div>
                      <div className="admin-table__mono muted">
                        {w.action_key}
                      </div>
                    </td>
                    <td className="muted">{w.unit}</td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        className="admin-table__input"
                        value={w.credits_per_unit}
                        onChange={(e) =>
                          editWeight(w, {
                            credits_per_unit: Number(e.target.value),
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={w.is_billable}
                        onChange={(e) =>
                          editWeight(w, { is_billable: e.target.checked })
                        }
                      />
                    </td>
                    <td className="muted">{w.notes ?? ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- edit modal ---------- */}
      {editing && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditing(null);
          }}
        >
          <form
            className="modal__card modal__card--lg modal__card--split"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
          >
            <header className="modal__header">
              <div className="modal__header-main">
                <h2 className="modal__header-title">
                  {editing.organization_name}
                </h2>
                <div className="muted">Billing settings</div>
              </div>
              <button
                type="button"
                className="modal__close"
                onClick={() => setEditing(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="modal__body">
              <div className="admin-card__grid">
                <div className="admin-card__field">
                  <label htmlFor="ob-tier">Tier</label>
                  <select
                    id="ob-tier"
                    value={form.tier_id}
                    onChange={(e) =>
                      setForm({ ...form, tier_id: e.target.value })
                    }
                  >
                    <option value="">No plan (not billed)</option>
                    {activeTiers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} - {money(t.base_price_usd)} /{" "}
                        {t.included_credits.toLocaleString()} credits
                      </option>
                    ))}
                    {/* An archived tier still assigned to this org must remain
                        selectable or saving would silently move them off it. */}
                    {tiers
                      .filter((t) => !t.is_active && t.id === editing.tier_id)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} (archived)
                        </option>
                      ))}
                  </select>
                </div>

                <div className="admin-card__field">
                  <label htmlFor="ob-start">Billing start date</label>
                  <input
                    id="ob-start"
                    type="date"
                    value={form.billing_start_date}
                    onChange={(e) =>
                      setForm({ ...form, billing_start_date: e.target.value })
                    }
                  />
                  <div className="admin-card__hint muted">
                    Sets the monthly anniversary. Backdating it lets the sweeper
                    generate the periods that have already elapsed.
                  </div>
                </div>

                <div className="admin-card__field">
                  <label htmlFor="ob-credits">Monthly credits override</label>
                  <input
                    id="ob-credits"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="inherit from tier"
                    value={form.monthly_credits}
                    onChange={(e) =>
                      setForm({ ...form, monthly_credits: e.target.value })
                    }
                  />
                </div>
                <div className="admin-card__field">
                  <label htmlFor="ob-base">Base price override (USD)</label>
                  <input
                    id="ob-base"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="inherit from tier"
                    value={form.base_price_usd}
                    onChange={(e) =>
                      setForm({ ...form, base_price_usd: e.target.value })
                    }
                  />
                </div>
                <div className="admin-card__field">
                  <label htmlFor="ob-overage">
                    Overage rate override (USD / credit)
                  </label>
                  <input
                    id="ob-overage"
                    type="number"
                    min="0"
                    step="0.000001"
                    placeholder="inherit from tier"
                    value={form.overage_rate_usd}
                    onChange={(e) =>
                      setForm({ ...form, overage_rate_usd: e.target.value })
                    }
                  />
                </div>
                <div className="admin-card__field">
                  <label htmlFor="ob-storage">
                    Storage override (GB / month)
                  </label>
                  <input
                    id="ob-storage"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="inherit from tier"
                    value={form.included_storage_gb}
                    onChange={(e) =>
                      setForm({ ...form, included_storage_gb: e.target.value })
                    }
                  />
                </div>
                <div className="admin-card__field">
                  <label htmlFor="ob-storage-rate">
                    Storage overage override (USD / GB)
                  </label>
                  <input
                    id="ob-storage-rate"
                    type="number"
                    min="0"
                    step="0.0001"
                    placeholder="inherit from tier"
                    value={form.storage_overage_usd_per_gb}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        storage_overage_usd_per_gb: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="admin-card__field">
                  <label htmlFor="ob-alert">Alert threshold (%)</label>
                  <input
                    id="ob-alert"
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={form.alert_threshold_pct}
                    onChange={(e) =>
                      setForm({ ...form, alert_threshold_pct: e.target.value })
                    }
                  />
                  <div className="admin-card__hint muted">
                    The point at which the customer's credit and storage
                    indicators turn amber.
                  </div>
                </div>
              </div>

              <p className="modal__desc muted">
                Leave an override blank to inherit the tier's value. Overrides
                take effect at the next period boundary - the open period keeps
                the prices it was created with.
              </p>
            </div>

            <footer className="modal__footer">
              <span className="muted modal__footer-hint">
                Current period: {fmtDate(editing.period_start)} to{" "}
                {fmtDate(editing.period_end)}
              </span>
              <div className="modal__footer-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setEditing(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save settings"}
                </button>
              </div>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
